import type {
  SessionAdvanceRoundParams,
  SessionAdvanceRoundResponse,
} from '@/app/api/copilot/geopolitical-actions/session/advance-round/route';
import type {
  SessionSimulateParams,
  SessionSimulateResponse,
} from '@/app/api/copilot/geopolitical-actions/session/simulate/route';
import type { CreateSessionParams, CreateSessionResponse } from '@/app/api/copilot/geopolitical-actions/session/types';
import type {
  GetGIActionsSimulationStreamParams,
  SimDecisionEvent,
  SimDialogueEvent,
  SimFinalEvent,
  SimLoadingEvent,
  SimStatusEvent,
  SimSubactorEnrichmentEvent,
  SimThoughtEvent,
  SimulateGIActionsParams,
  SimulateGIActionsResponse,
} from '@/app/api/copilot/geopolitical-actions/types';
import { ROUTE, axiosInstance } from './axiosInstance';

export type {
  GetGIActionsSimulationStreamParams,
  SimDecisionEvent,
  SimDecisionOption,
  SimDialogueEvent,
  SimFinalDecision,
  SimFinalEvent,
  SimLoadingEvent,
  SimStatusEvent,
  SimSubactorEnrichmentEvent,
  SimThoughtEvent,
  SimulateGIActionsParams,
  SimulateGIActionsResponse,
} from '@/app/api/copilot/geopolitical-actions/types';
export type { CreateSessionParams, CreateSessionResponse } from '@/app/api/copilot/geopolitical-actions/session/types';
export type {
  SessionSimulateParams,
  SessionSimulateResponse,
} from '@/app/api/copilot/geopolitical-actions/session/simulate/route';
export type {
  SessionAdvanceRoundParams,
  SessionAdvanceRoundResponse,
} from '@/app/api/copilot/geopolitical-actions/session/advance-round/route';

const consumeSimulationStream = async (
  streamUrl: string,
  {
    onLoading,
    onStatus,
    onThoughtToken,
    onDecision,
    onFinal,
    onError,
    onSubactorEnrichment,
    onDialogue,
  }: GetGIActionsSimulationStreamParams,
): Promise<void> => {
  const res = await fetch(streamUrl, { method: 'GET' });

  if (!res.ok) {
    const errorText = await res.text();
    onError(new Error(`Stream error ${res.status}: ${errorText}`));
    return;
  }
  if (!res.body) {
    onError(new Error('No response body'));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let partial = '';
  let currentEvent = '';
  let done = false;

  const dispatchLine = (line: string): boolean => {
    if (!line.trim() || line.trim() === ': keepalive') return false;

    if (line.startsWith('event:')) {
      currentEvent = line.substring(6).trim();
    } else if (line.startsWith('data:')) {
      const jsonData = line.substring(5).trim();
      if (!jsonData) return false;

      try {
        const data = JSON.parse(jsonData);

        if (currentEvent === 'loading') {
          onLoading?.(data as SimLoadingEvent);
        } else if (currentEvent === 'status') {
          onStatus?.(data as SimStatusEvent);
        } else if (currentEvent === 'subactor_enrichment') {
          onSubactorEnrichment?.(data as SimSubactorEnrichmentEvent);
        } else if (currentEvent === 'dialogue') {
          onDialogue?.(data as SimDialogueEvent);
        } else if (currentEvent === 'thought') {
          onThoughtToken(data as SimThoughtEvent);
        } else if (currentEvent === 'decision') {
          onDecision(data as SimDecisionEvent);
        } else if (currentEvent === 'final') {
          onFinal(data as SimFinalEvent);
          return true;
        } else if (currentEvent === 'error') {
          onError(new Error((data as { error: string }).error));
          return true;
        }
      } catch (err) {
        onError(err as Error);
      }
    }

    return false;
  };

  while (!done) {
    const { value, done: doneReading } = await reader.read();
    if (doneReading) {
      done = doneReading;
      break;
    }

    if (value) {
      partial += decoder.decode(value, { stream: true });
      const parts = partial.split('\n');
      partial = parts.pop()?.trim() ?? '';

      for (const line of parts) {
        if (dispatchLine(line)) {
          done = true;
          break;
        }
      }
    }
  }

  // Flush any complete event remaining in the buffer after the stream closes
  if (partial.trim()) {
    for (const line of partial.split('\n')) {
      if (dispatchLine(line)) break;
    }
  }
};

/** Geopolitical Intelligence Actions copilot query */
export const copilotGiActions = {
  /** Start a GI actions simulation. Returns task_id for streaming. */
  simulateGIActions: async (params: SimulateGIActionsParams): Promise<SimulateGIActionsResponse> => {
    const { data } = await axiosInstance.post<SimulateGIActionsResponse>(ROUTE.COPILOT.QUERY_GI_ACTIONS, params);
    return data;
  },

  /** Connect to the SSE stream for a simulation task and dispatch events via callbacks. */
  getGIActionsSimulationStream: async (params: GetGIActionsSimulationStreamParams): Promise<void> => {
    const url = new URL(`/api${ROUTE.COPILOT.GI_ACTIONS_STREAM}`, window.location.origin);
    url.searchParams.append('task_id', params.task_id);
    await consumeSimulationStream(url.toString(), params);
  },

  /** Connect to the SSE stream for a session simulate task and dispatch events via callbacks. */
  getSessionSimulationStream: async (params: GetGIActionsSimulationStreamParams): Promise<void> => {
    const url = new URL(`/api${ROUTE.COPILOT.GI_ACTIONS_SESSION_SIMULATE_STREAM}`, window.location.origin);
    url.searchParams.append('task_id', params.task_id);
    await consumeSimulationStream(url.toString(), params);
  },

  getSession: async (sessionId: string): Promise<CreateSessionResponse> => {
    const { data } = await axiosInstance.get<CreateSessionResponse>(ROUTE.COPILOT.GI_ACTIONS_SESSION, {
      params: { session_id: sessionId },
    });
    return data;
  },

  createSession: async (params: CreateSessionParams): Promise<CreateSessionResponse> => {
    const { data } = await axiosInstance.post<CreateSessionResponse>(ROUTE.COPILOT.GI_ACTIONS_SESSION, params);
    return data;
  },

  sessionSimulate: async (params: SessionSimulateParams): Promise<SessionSimulateResponse> => {
    const { data } = await axiosInstance.post<SessionSimulateResponse>(
      ROUTE.COPILOT.GI_ACTIONS_SESSION_SIMULATE,
      params,
    );
    return data;
  },

  sessionAdvanceRound: async (params: SessionAdvanceRoundParams): Promise<SessionAdvanceRoundResponse> => {
    const { data } = await axiosInstance.post<SessionAdvanceRoundResponse>(
      ROUTE.COPILOT.GI_ACTIONS_SESSION_ADVANCE_ROUND,
      params,
    );
    return data;
  },
};
